import {
  handleForcedSignOut,
  type ForcedSignOutReason,
} from "@/lib/session/session-logout";

import { ApiError, parseApiError } from "./error";
import { getApiBaseUrl } from "./base-url";

function apiBaseUrl(): string {
  return getApiBaseUrl();
}

let refreshPromise: Promise<void> | null = null;

function getForcedSignOutReason(error: ApiError): ForcedSignOutReason | null {
  if (error.code === "SESSION_DISPLACED") return "another_device";
  if (error.code === "SESSION_REVOKED") return "session_ended";
  return null;
}

function handleForcedSignOutError(error: ApiError): boolean {
  const reason = getForcedSignOutReason(error);
  if (!reason) return false;
  handleForcedSignOut(reason);
  return true;
}

async function doRefresh(): Promise<void> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const res = await fetch(`${apiBaseUrl()}/api/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const error = parseApiError(body, res.status);
      // MISSING_REFRESH_TOKEN means the caller never had a session (no
      // refresh cookie present), e.g. a logged-out visitor opening an
      // invite link. That's not a session-loss event and must not
      // trigger a forced redirect to /login — pages like /invite and
      // /invited expect to render their unauthenticated UI in that
      // state. Only redirect for true session-loss codes
      // (SESSION_DISPLACED, SESSION_REVOKED) handled below, or for an
      // unrecognized 401 from refresh which still indicates a broken
      // session.
      if (
        !handleForcedSignOutError(error) &&
        error.code !== "MISSING_REFRESH_TOKEN"
      ) {
        handleForcedSignOut();
      }
      throw error;
    }
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

function applyHeaders(options: RequestInit): Headers {
  const isFormData = options.body instanceof FormData;
  const headers = new Headers(options.headers);
  if (!isFormData && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  return headers;
}

async function getForcedSignOutReasonFromResponse(
  res: Response,
): Promise<ForcedSignOutReason | null> {
  const body = await res
    .clone()
    .json()
    .catch(() => null);
  return getForcedSignOutReason(parseApiError(body, res.status));
}

export async function authenticatedRawFetch(
  url: string,
  options: RequestInit = {},
): Promise<Response> {
  const firstRes = await fetch(`${apiBaseUrl()}${url}`, {
    ...options,
    headers: applyHeaders(options),
    credentials: "include",
  });

  if (firstRes.status !== 401) return firstRes;

  const initialForcedSignOutReason =
    await getForcedSignOutReasonFromResponse(firstRes);
  if (initialForcedSignOutReason === "another_device") {
    handleForcedSignOut(initialForcedSignOutReason);
    return firstRes;
  }

  try {
    await doRefresh();
  } catch {
    if (initialForcedSignOutReason === "session_ended") {
      handleForcedSignOut("session_ended");
    }
    return firstRes;
  }

  const nextRes = await fetch(`${apiBaseUrl()}${url}`, {
    ...options,
    headers: applyHeaders(options),
    credentials: "include",
  });

  if (nextRes.status === 401) {
    const retryForcedSignOutReason =
      await getForcedSignOutReasonFromResponse(nextRes);
    if (retryForcedSignOutReason) {
      handleForcedSignOut(retryForcedSignOutReason);
    }
  }

  return nextRes;
}

async function doFetch<T>(url: string, options: RequestInit): Promise<T> {
  const headers = applyHeaders(options);

  const res = await fetch(`${apiBaseUrl()}${url}`, {
    ...options,
    headers,
    credentials: "include",
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw parseApiError(body, res.status);
  }

  if (res.status === 204) return undefined as T;

  const payload = await res.json();
  if (payload && typeof payload === "object" && "data" in payload) {
    return payload.data as T;
  }
  return payload as T;
}

const AUTH_PATHS = new Set([
  "/api/auth/login",
  "/api/auth/register",
  "/api/auth/refresh",
  "/api/auth/mfa-verify",
  "/api/auth/verify-email",
  "/api/auth/forgot-password",
  "/api/auth/reset-password",
  "/api/auth/resend-verification",
  "/api/auth/invitations/accept",
]);

function isAuthPath(url: string): boolean {
  return AUTH_PATHS.has(url) || url.startsWith("/api/auth/invitations/");
}

export const customInstance = async <T>(
  url: string,
  options: RequestInit = {},
): Promise<T> => {
  if (isAuthPath(url)) {
    return doFetch<T>(url, options);
  }

  try {
    return await doFetch<T>(url, options);
  } catch (err: unknown) {
    if (err instanceof ApiError && err.status === 401) {
      if (err.code === "SESSION_DISPLACED") {
        handleForcedSignOut("another_device");
        throw err;
      }

      await doRefresh();

      try {
        return await doFetch<T>(url, options);
      } catch (retryErr: unknown) {
        if (
          retryErr instanceof ApiError &&
          retryErr.status === 401 &&
          handleForcedSignOutError(retryErr)
        ) {
          throw retryErr;
        }
        throw retryErr;
      }
    }
    throw err;
  }
};
